import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { useIntake } from '../hooks/useIntake';
import { useIntakeDocuments } from '../hooks/useIntakeDocuments';
import type { AgencyInsuranceResponse } from '../services/api/types.gen';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  PanelLeftClose,
  PanelLeftOpen,
  MessageSquare,
  X,
  UserCircle2,
} from 'lucide-react';
import {
  type IntakeResponse,
  getIntakeDocumentsNameDict,
  client,
} from '../services/api';
import PDFViewerNative from '../components/PDFViewerNative';
import EligibilityCheckStep from '../components/intake/EligibilityCheckStep';
import SchedulingStep from '../components/intake/SchedulingStep';
import CreateEpisodeStep from '../components/intake/CreateEpisodeStep';
import PriorAuthStep from '../components/intake/PriorAuthStep';
import { ExtractedFields } from '../components/intake/ExtractedInfoStep';
import {
  AutoSaveComments,
  ChecklistItem,
  PatientNotes,
  InlineEditableField,
  InlineEditableDateField,
} from '../components/intake/shared';
import MarkCompletedButton from '../components/intake/MarkCompletedButton';
import { AiExtractedInsuranceSuggestion } from '../components/intake/AiExtractedInsuranceSuggestion';
import { INTAKE_STATUS } from '../constants/intakeStatus';
import { FeatureFlag } from '../constants/featureFlags';
import WorkflowStepper, {
  type WorkflowStep,
} from '../components/intake/WorkflowStepper';
import type {
  CodingExtractionItem,
  ChecklistItemDetails,
} from '../types/codingData.types';
import { IntakeUserAssignmentDialog } from '../components/IntakeUserAssignmentDialog';
import { useToast } from '../hooks/useToastContext';

const INTAKE_TAB_NAMES = {
  EXTRACTION: 'extraction',
  ELIGIBILITY: 'eligibility',
  PRIOR_AUTH: 'prior-auth',
  READY_TO_CALL: 'ready-to-call',
  SCHEDULING: 'scheduling',
  PUSH_TO_EMR: 'push-to-emr',
} as const;

type IntakeTabValue = (typeof INTAKE_TAB_NAMES)[keyof typeof INTAKE_TAB_NAMES];

const Intake3: React.FC = () => {
  const { intakeId } = useParams<{ intakeId: string }>();
  const navigate = useNavigate();
  const {
    hasPermission,
    hasFeatureFlag,
    agencyInsurances,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
    shouldRefreshAgencyInsurances,
    agencyClinics,
    agencyClinicsLoading,
    fetchAgencyClinics,
    agencyInsurancesError,
    agencyClinicsError,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      agencyInsurances: state.agencyInsurances,
      agencyInsurancesLoading: state.agencyInsurancesLoading,
      fetchAgencyInsurances: state.fetchAgencyInsurances,
      shouldRefreshAgencyInsurances: state.shouldRefreshAgencyInsurances,
      agencyClinics: state.agencyClinics,
      agencyClinicsLoading: state.agencyClinicsLoading,
      fetchAgencyClinics: state.fetchAgencyClinics,
      agencyInsurancesError: state.agencyInsurancesError,
      agencyClinicsError: state.agencyClinicsError,
    }))
  );

  // Calculate visible tab count for grid layout
  const hasEligibilityFeatureFlag = hasFeatureFlag(
    FeatureFlag.INTAKE_ELIGIBILITY_CHECK_SUPPORT
  );
  const hasPriorAuthFeatureFlag = hasFeatureFlag(
    FeatureFlag.INTAKE_PRIOR_AUTH_SUPPORT
  );
  const hasReadyToCallFeatureFlag = hasFeatureFlag(
    FeatureFlag.INTAKE_READY_TO_CALL_SUPPORT
  );
  const hasSchedulingFeatureFlag = hasFeatureFlag(
    FeatureFlag.INTAKE_SCHEDULING_SUPPORT
  );
  const visibleTabCount =
    1 +
    (hasEligibilityFeatureFlag ? 1 : 0) +
    (hasPriorAuthFeatureFlag ? 1 : 0) +
    (hasReadyToCallFeatureFlag ? 1 : 0) +
    (hasSchedulingFeatureFlag ? 1 : 0) +
    (hasSchedulingFeatureFlag ? 1 : 0); // Add create episode tab when scheduling is enabled

  // Use intakeSlice store
  const {
    selectedIntake,
    loadIntakeDetails,
    updateIntakeRecord,
    updateIntakeStatusRecord,
  } = useIntake();

  // Document management hook
  const {
    documentUrls,
    activeDocumentId,
    setActiveDocumentId,
    handleDocumentRename,
    handleDocumentAdd,
    handleRefreshDocumentUrl,
  } = useIntakeDocuments({
    intakeId,
    documents: selectedIntake?.documents,
    loadIntakeDetails,
  });

  // Keep UI-specific state local
  const [formData, setFormData] = useState<Partial<IntakeResponse>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<IntakeTabValue>(
    INTAKE_TAB_NAMES.EXTRACTION
  );
  const hasFetchedAgencyInsurancesRef = React.useRef(false);

  // Sticky PDF viewer logic
  const pdfWrapperRef = React.useRef<HTMLDivElement>(null);
  const [isPdfSticky, setIsPdfSticky] = useState(false);
  const [pdfStickyLeft, setPdfStickyLeft] = useState(0);
  const [pdfStickyWidth, setPdfStickyWidth] = useState(0);
  const [pdfStickyHeight, setPdfStickyHeight] = useState(0);
  const [isAssigneeDialogOpen, setIsAssigneeDialogOpen] = useState(false);
  const [documentNameDict, setDocumentNameDict] = useState<
    Record<string, string>
  >({});

  // Toast
  const { error: showToastError } = useToast();

  useEffect(() => {
    const handleScroll = () => {
      const pdfWrapper = pdfWrapperRef.current;
      if (!pdfWrapper) return;

      const wrapperRect = pdfWrapper.getBoundingClientRect();
      const stickyThreshold = 0;
      const shouldStick = wrapperRect.top < stickyThreshold;

      if (shouldStick && !isPdfSticky) {
        setPdfStickyLeft(wrapperRect.left);
        setPdfStickyWidth(wrapperRect.width);
        setPdfStickyHeight(wrapperRect.height);
        setIsPdfSticky(true);
      } else if (!shouldStick && isPdfSticky) {
        setIsPdfSticky(false);
      }
    };

    const handleResize = () => setIsPdfSticky(false);

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isPdfSticky]);

  // Check permission - redirect if user doesn't have intake:access
  useEffect(() => {
    if (!hasPermission('intake:access')) {
      navigate('/dashboard');
    }
  }, [hasPermission, navigate]);

  // Fetch agency insurances on component mount (use cached data if available and fresh)
  useEffect(() => {
    if (
      !hasFetchedAgencyInsurancesRef.current &&
      shouldRefreshAgencyInsurances() &&
      !agencyInsurancesLoading
    ) {
      hasFetchedAgencyInsurancesRef.current = true;
      fetchAgencyInsurances();
    }
  }, [
    shouldRefreshAgencyInsurances,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
  ]);

  // Fetch agency clinics on mount
  const hasFetchedAgencyClinicsRef = React.useRef(false);
  useEffect(() => {
    if (
      !hasFetchedAgencyClinicsRef.current &&
      agencyClinics.length === 0 &&
      !agencyClinicsLoading
    ) {
      hasFetchedAgencyClinicsRef.current = true;
      fetchAgencyClinics();
    }
  }, [agencyClinics.length, agencyClinicsLoading, fetchAgencyClinics]);

  useEffect(() => {
    const loadIntake = async () => {
      if (!intakeId) {
        setError('No intake ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const intake = await loadIntakeDetails(intakeId);

        if (intake) {
          // Convert IntakeRecord to IntakeResponse for form data
          setFormData(intake as unknown as Partial<IntakeResponse>);
        } else {
          setError('Intake not found');
        }
      } catch (err) {
        console.error('Error loading intake:', err);
        setError('Failed to load intake');
      } finally {
        setLoading(false);
      }
    };

    loadIntake();
  }, [intakeId, loadIntakeDetails]);

  // Sync formData when selectedIntake changes (e.g., when other steps save data)
  useEffect(() => {
    if (selectedIntake) {
      setFormData(selectedIntake as unknown as Partial<IntakeResponse>);
    }
  }, [selectedIntake]);

  // Load document name dict (upload name -> display name) for extraction tab
  useEffect(() => {
    if (!intakeId) return;
    let cancelled = false;
    getIntakeDocumentsNameDict({
      path: { intake_id: intakeId },
      client,
    })
      .then(res => {
        if (cancelled) return;
        const data = res.data as { name_dict?: Record<string, string> };
        setDocumentNameDict(data?.name_dict ?? {});
      })
      .catch(() => {
        if (!cancelled) setDocumentNameDict({});
      });
    return () => {
      cancelled = true;
    };
  }, [intakeId, selectedIntake?.documents]);

  // Construct agency clinic options
  const agencyClinicOptions = agencyClinics.map(clinic => ({
    value: clinic.id,
    label: clinic.name,
  }));

  // Construct agency insurance options
  const agencyInsuranceOptions = [
    {
      value: '',
      label: 'None',
    },
    ...(() => {
      // Find the selected clinic to get its clinic_key
      const selectedClinic = agencyClinics.find(
        clinic => clinic.id === formData.agency_clinic_id
      );

      // Filter insurances to only those matching the selected clinic's clinic_key
      const filteredInsurances = selectedClinic?.clinic_key
        ? agencyInsurances.filter(
            (agencyInsurance: AgencyInsuranceResponse) =>
              agencyInsurance.clinic_key === selectedClinic.clinic_key
          )
        : agencyInsurances; // Show all if no clinic selected

      // Deduplicate by name + edi_payer_id combination
      // Eligibility indicator prefix (check mark + two non-breaking spaces)
      const ELIGIBILITY_INDICATOR = '✓\u00A0\u00A0';

      const uniqueInsurances = new Map();
      filteredInsurances.forEach((agencyInsurance: AgencyInsuranceResponse) => {
        const key = `${agencyInsurance.name}_${agencyInsurance.edi_payer_id}`;
        if (!uniqueInsurances.has(key)) {
          // Check if eligibility is supported (has stedi_eligibility_enabled from linked payor)
          const supportsEligibility = Boolean(
            agencyInsurance.stedi_eligibility_enabled
          );
          uniqueInsurances.set(key, {
            value: agencyInsurance.id,
            label: `${supportsEligibility ? ELIGIBILITY_INDICATOR : ''}${agencyInsurance.name}${agencyInsurance.edi_payer_id ? ` (${agencyInsurance.edi_payer_id})` : ''}`,
          });
        }
      });

      // Sort: eligibility-supported first, then alphanumeric, then non-alphanumeric
      return Array.from(uniqueInsurances.values()).sort((a, b) => {
        // First sort by eligibility support (✓ comes first)
        const aSupportsElig = a.label.startsWith(ELIGIBILITY_INDICATOR);
        const bSupportsElig = b.label.startsWith(ELIGIBILITY_INDICATOR);
        if (aSupportsElig && !bSupportsElig) return -1;
        if (!aSupportsElig && bSupportsElig) return 1;

        // Then sort alphanumeric first, then non-alphanumeric
        // Strip the eligibility indicator prefix for comparison
        const aLabelClean = a.label.replace(ELIGIBILITY_INDICATOR, '');
        const bLabelClean = b.label.replace(ELIGIBILITY_INDICATOR, '');
        const aStartsWithAlphanum = /^[a-zA-Z0-9]/.test(aLabelClean);
        const bStartsWithAlphanum = /^[a-zA-Z0-9]/.test(bLabelClean);

        if (aStartsWithAlphanum && !bStartsWithAlphanum) return -1;
        if (!aStartsWithAlphanum && bStartsWithAlphanum) return 1;

        return aLabelClean.localeCompare(bLabelClean);
      });
    })(),
  ];

  if (loading) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  if (error || !selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-600 mb-4'>{error || 'Intake not found'}</p>
          <Button onClick={() => navigate('/intake-manager')}>
            Back to Intake Manager
          </Button>
        </div>
      </div>
    );
  }

  // Replace each key in name_dict with its display value in text (e.g. filenames -> display names)
  const replaceKeysWithDisplayNames = (
    text: string | null | undefined,
    nameDict: Record<string, string>
  ): string | null | undefined => {
    if (text == null || text === '') return text;
    const keys = Object.keys(nameDict).filter(k => k.length > 0);
    let result = text;
    for (const key of keys) {
      result = result.split(key).join(nameDict[key]);
    }
    return result;
  };

  // Type for coding extraction from API
  interface CodingExtraction {
    face_to_face_visits?: CodingExtractionItem[];
    home_health_orders?: CodingExtractionItem[];
    authorized_diagnoses?: CodingExtractionItem[];
    not_qualifying_face_to_face_visits_reasoning?: string | null;
    not_qualifying_home_health_orders_reasoning?: string | null;
  }

  // Calculate checklist statuses with details from coding extraction
  const getChecklistStatusesWithDetails = (): {
    statuses: Record<string, 'complete' | 'missing' | 'not-applicable'>;
    details: Record<string, ChecklistItemDetails>;
  } => {
    // Get coding extraction data from the intake
    const codingExtraction = (
      selectedIntake as unknown as IntakeResponse & {
        coding_extraction?: CodingExtraction;
      }
    ).coding_extraction;

    // Get arrays from coding extraction
    const homeHealthOrders = codingExtraction?.home_health_orders ?? [];
    const faceToFaceVisits = codingExtraction?.face_to_face_visits ?? [];

    // Check if arrays have items (for "Attached" status)
    const hasOrdersFromCoding = homeHealthOrders.length > 0;
    const hasF2FFromCoding = faceToFaceVisits.length > 0;

    // Check if any item has is_signed.value = true (for "Signed" status)
    const hasSignedOrder = homeHealthOrders.some(
      order => order.validations?.is_signed?.value === true
    );
    const hasSignedF2F = faceToFaceVisits.some(
      f2f => f2f.validations?.is_signed?.value === true
    );

    // Order Attached: Green if home_health_orders array is not empty
    let orderAttachedStatus: 'complete' | 'missing' | 'not-applicable';
    if (hasOrdersFromCoding) {
      orderAttachedStatus = 'complete';
    } else if (codingExtraction?.not_qualifying_home_health_orders_reasoning) {
      orderAttachedStatus = 'missing';
    } else {
      orderAttachedStatus = 'missing';
    }

    // Order Signed: Green if at least one order has is_signed.value = true
    // Red only if ALL orders have is_signed.value = false (or no orders exist)
    let orderSignedStatus: 'complete' | 'missing' | 'not-applicable';
    if (hasSignedOrder) {
      orderSignedStatus = 'complete';
    } else if (hasOrdersFromCoding) {
      // Has orders but none are signed
      orderSignedStatus = 'missing';
    } else {
      // No orders at all
      orderSignedStatus = 'missing';
    }

    // F2F Attached: Green if face_to_face_visits array is not empty
    let f2fAttachedStatus: 'complete' | 'missing' | 'not-applicable';
    if (hasF2FFromCoding) {
      f2fAttachedStatus = 'complete';
    } else if (codingExtraction?.not_qualifying_face_to_face_visits_reasoning) {
      f2fAttachedStatus = 'missing';
    } else {
      f2fAttachedStatus = 'missing';
    }

    // F2F Signed: Green if at least one F2F has is_signed.value = true
    // Red only if ALL F2Fs have is_signed.value = false (or no F2Fs exist)
    let f2fSignedStatus: 'complete' | 'missing' | 'not-applicable';
    if (hasSignedF2F) {
      f2fSignedStatus = 'complete';
    } else if (hasF2FFromCoding) {
      // Has F2F visits but none are signed
      f2fSignedStatus = 'missing';
    } else {
      // No F2F visits at all
      f2fSignedStatus = 'missing';
    }

    // Build details for items
    // For "Attached" fields: show all items with their reasoning
    // For "Signed" fields: show only items where is_signed.value is true (with is_signed reasoning)
    const details: Record<string, ChecklistItemDetails> = {
      // Order Attached shows all orders with their reasoning
      orderAttached: {
        items: homeHealthOrders,
        notQualifyingReasoning: replaceKeysWithDisplayNames(
          codingExtraction?.not_qualifying_home_health_orders_reasoning,
          documentNameDict
        ),
      },
      // Order Signed shows only signed orders, with is_signed reasoning displayed
      orderSigned: {
        items: homeHealthOrders,
        showOnlySignedItems: true,
        // If no orders at all, show the not qualifying reasoning
        notQualifyingReasoning: !hasOrdersFromCoding
          ? replaceKeysWithDisplayNames(
              codingExtraction?.not_qualifying_home_health_orders_reasoning,
              documentNameDict
            )
          : undefined,
      },
      // F2F Attached shows all F2F visits with their reasoning
      f2fAttached: {
        items: faceToFaceVisits,
        notQualifyingReasoning: replaceKeysWithDisplayNames(
          codingExtraction?.not_qualifying_face_to_face_visits_reasoning,
          documentNameDict
        ),
      },
      // F2F Signed shows only signed F2F visits, with is_signed reasoning displayed
      f2fSigned: {
        items: faceToFaceVisits,
        showOnlySignedItems: true,
        // If no F2F visits at all, show the not qualifying reasoning
        notQualifyingReasoning: !hasF2FFromCoding
          ? replaceKeysWithDisplayNames(
              codingExtraction?.not_qualifying_face_to_face_visits_reasoning,
              documentNameDict
            )
          : undefined,
      },
    };

    return {
      statuses: {
        orderSigned: orderSignedStatus,
        orderAttached: orderAttachedStatus,
        f2fSigned: f2fSignedStatus,
        f2fAttached: f2fAttachedStatus,
        hAndP: formData.past_medical_history ? 'complete' : 'missing',
      },
      details,
    };
  };

  const { statuses: checklistStatuses, details: checklistDetails } =
    getChecklistStatusesWithDetails();

  // Helper function to check if eligibility check tab is accessible
  // Requires extraction tab to be complete
  const canAccessEligibilityCheck = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if create episode tab is accessible
  // Requires extraction tab to be complete
  const canAccessCreateEpisode = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if prior auth tab is accessible
  // Requires extraction tab to be complete
  const canAccessPriorAuth = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if ready to call tab is accessible
  // Requires extraction tab to be complete
  const canAccessReadyToCall = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if scheduling tab is accessible
  // Requires extraction tab to be complete
  const canAccessScheduling = () => {
    return isTabComplete('extraction');
  };

  // Tab completion check - uses tab_statuses field
  const isTabComplete = (tabKey: string): boolean => {
    const tabStatus =
      selectedIntake?.tab_statuses?.[
        tabKey as keyof typeof selectedIntake.tab_statuses
      ];
    return tabStatus === 'complete' || tabStatus === 'complete_with_errors';
  };

  // Handler for saving patient notes
  const handleSaveNotes = async (notes: string) => {
    try {
      await updateIntakeRecord(intakeId!, { patient_notes: notes });
      setFormData(prev => ({ ...prev, patient_notes: notes }));
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  // Handler for clearing the assignee
  const handleClearAssignee = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateIntakeRecord(intakeId!, { assigned_to: null });
      await loadIntakeDetails(intakeId!);
    } catch (error) {
      console.error('Error clearing assignee:', error);
      showToastError('Failed to clear assignee.');
    }
  };

  // Helper function to validate and set active tab
  const handleTabChange = (value: string) => {
    if (
      value === INTAKE_TAB_NAMES.EXTRACTION ||
      value === INTAKE_TAB_NAMES.ELIGIBILITY ||
      value === INTAKE_TAB_NAMES.PRIOR_AUTH ||
      value === INTAKE_TAB_NAMES.READY_TO_CALL ||
      value === INTAKE_TAB_NAMES.SCHEDULING ||
      value === INTAKE_TAB_NAMES.PUSH_TO_EMR
    ) {
      setActiveTab(value as IntakeTabValue);
    }
  };

  // Handler for when extraction tab status changes (called by MarkCompletedButton)
  const handleExtractionStatusChange = async () => {
    // Store the current extraction status before the API call updates it
    const wasComplete =
      selectedIntake?.tab_statuses?.extraction === 'complete' ||
      selectedIntake?.tab_statuses?.extraction === 'complete_with_errors';

    // Reload intake from API to get the updated state after MarkCompletedButton's API call
    if (intakeId) {
      await loadIntakeDetails(intakeId);
    }

    // Only move to the next tab if extraction was just marked as complete (wasn't complete before)
    if (!wasComplete) {
      // Move to the next available tab after marking extraction complete
      if (hasEligibilityFeatureFlag) {
        setActiveTab(INTAKE_TAB_NAMES.ELIGIBILITY);
      } else if (hasPriorAuthFeatureFlag) {
        setActiveTab(INTAKE_TAB_NAMES.PRIOR_AUTH);
      } else if (hasReadyToCallFeatureFlag) {
        setActiveTab(INTAKE_TAB_NAMES.READY_TO_CALL);
      } else if (hasSchedulingFeatureFlag) {
        setActiveTab(INTAKE_TAB_NAMES.SCHEDULING);
      }
    }
  };

  return (
    <div className='bg-background flex flex-col'>
      {/* Header */}
      <div className='border-b bg-background flex-shrink-0'>
        {/* Back Button Bar */}
        <div className='px-6 pt-3 pb-2 border-b border-gray-100 flex items-center justify-between'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => navigate('/intake-manager')}
            className='flex items-center gap-1.5'
          >
            <ArrowLeft className='h-3.5 w-3.5' />
            Back to Intake Manager
          </Button>
        </div>

        {/* Patient Info Row */}
        <div className='px-6 py-3'>
          <div className='flex items-start justify-between gap-4'>
            <div className='flex-1'>
              <div className='flex items-center gap-3 mb-2'>
                <h1 className='text-2xl font-bold text-gray-900'>
                  {formData.patient_first_name} {formData.patient_last_name}
                </h1>
                <PatientNotes
                  notes={formData.patient_notes || ''}
                  patientName={`${formData.patient_first_name} ${formData.patient_last_name}`}
                  phoneNumber={formData.patient_phone || 'N/A'}
                  onSave={handleSaveNotes}
                />
                <div className='group relative'>
                  <button
                    onClick={() => setIsAssigneeDialogOpen(true)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-all ${
                      selectedIntake.assigned_to_user
                        ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                    }`}
                    title={
                      selectedIntake.assigned_to_user
                        ? 'Click to reassign'
                        : 'Click to assign'
                    }
                  >
                    <UserCircle2 className='h-3.5 w-3.5' />
                    <span>
                      {selectedIntake.assigned_to_user?.email || 'Unassigned'}
                    </span>
                    {selectedIntake.assigned_to_user && (
                      <button
                        onClick={handleClearAssignee}
                        className='ml-0.5 -mr-0.5 p-0.5 rounded-full hover:bg-blue-200 transition-colors'
                        title='Clear assignee'
                      >
                        <X className='h-3 w-3' />
                      </button>
                    )}
                  </button>
                </div>
              </div>
              <div className='text-sm text-muted-foreground flex flex-wrap items-center gap-x-1 gap-y-0.5'>
                <InlineEditableDateField
                  label='DOB'
                  value={formData.patient_dob}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, patient_dob: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      patient_dob: value,
                    });
                  }}
                  showAge
                  yearRange={{
                    fromYear: new Date().getFullYear() - 120,
                    toYear: new Date().getFullYear(),
                  }}
                />
                <span>•</span>
                <InlineEditableField
                  label='Insurance'
                  value={formData.agency_insurance?.name || ''}
                  disabled={true}
                  onChange={() => {}}
                  onBlur={() => {}}
                  placeholder='No Insurance'
                />
                <span>•</span>
                <InlineEditableField
                  label='Phone'
                  value={formData.patient_phone || ''}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, patient_phone: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      patient_phone: value,
                    });
                  }}
                />
                <span>•</span>
                <InlineEditableDateField
                  label='Referral'
                  value={formData.referral_date}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, referral_date: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      referral_date: value,
                    });
                  }}
                />
                <span>•</span>
                <InlineEditableDateField
                  label='Order'
                  value={formData.physician_order_date}
                  onChange={value => {
                    setFormData(prev => ({
                      ...prev,
                      physician_order_date: value,
                    }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      physician_order_date: value,
                    });
                  }}
                />
                <span>•</span>
                <InlineEditableDateField
                  label='Discharge'
                  value={formData.discharge_date}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, discharge_date: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      discharge_date: value,
                    });
                  }}
                />
                <span>•</span>
                <InlineEditableDateField
                  label='F2F'
                  value={formData.f2f_encounter_date}
                  onChange={value => {
                    setFormData(prev => ({
                      ...prev,
                      f2f_encounter_date: value,
                    }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      f2f_encounter_date: value,
                    });
                  }}
                />
              </div>
            </div>
            <div className='flex flex-col items-end gap-2'>
              <div
                className={`inline-flex items-center rounded-full border border-transparent font-semibold text-xs px-3 py-1 flex-shrink-0 ${
                  selectedIntake.status === INTAKE_STATUS.PENDING_REVIEW
                    ? 'bg-orange-100 text-orange-800'
                    : selectedIntake.status ===
                          INTAKE_STATUS.PUSHED_TO_KINNSER ||
                        selectedIntake.status === INTAKE_STATUS.PUSHED_TO_ROGER
                      ? 'bg-green-700 text-white border-green-900'
                      : [
                            INTAKE_STATUS.ACCEPTED,
                            INTAKE_STATUS.SCHEDULED,
                          ].includes(selectedIntake.status)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                }`}
              >
                {selectedIntake.status === INTAKE_STATUS.PENDING_REVIEW
                  ? 'Pending Review'
                  : selectedIntake.status === INTAKE_STATUS.ACCEPTED
                    ? 'Accepted'
                    : selectedIntake.status === INTAKE_STATUS.REJECTED
                      ? 'Not Admitted'
                      : selectedIntake.status
                          ?.split('_')
                          .map(
                            word =>
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase()
                          )
                          .join(' ') || 'Unknown'}
              </div>
              {![
                INTAKE_STATUS.PUSHED_TO_KINNSER,
                INTAKE_STATUS.PUSHED_TO_ROGER,
                INTAKE_STATUS.REJECTED,
                INTAKE_STATUS.DUPLICATE,
                INTAKE_STATUS.EXPIRED,
              ].includes(selectedIntake.status) && (
                <Button
                  size='default'
                  variant='outline'
                  disabled={isUpdatingStatus}
                  onClick={async () => {
                    setIsUpdatingStatus(true);
                    try {
                      await updateIntakeStatusRecord(
                        intakeId!,
                        INTAKE_STATUS.REJECTED
                      );
                      // Refresh the intake data
                      await loadIntakeDetails(intakeId!);
                      setFormData(prev => ({
                        ...prev,
                        status: INTAKE_STATUS.REJECTED,
                      }));
                    } catch (error) {
                      console.error('Error rejecting intake:', error);
                      alert('Failed to reject intake. Please try again.');
                    } finally {
                      setIsUpdatingStatus(false);
                    }
                  }}
                >
                  {isUpdatingStatus ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <X className='h-4 w-4 text-red-600' />
                  )}
                  Non-admit
                </Button>
              )}
              {selectedIntake.status === INTAKE_STATUS.REJECTED && (
                <Button
                  size='default'
                  variant='outline'
                  disabled={isUpdatingStatus}
                  onClick={async () => {
                    setIsUpdatingStatus(true);
                    try {
                      await updateIntakeStatusRecord(
                        intakeId!,
                        INTAKE_STATUS.PENDING_REVIEW
                      );
                      // Refresh the intake data
                      await loadIntakeDetails(intakeId!);
                      setFormData(prev => ({
                        ...prev,
                        status: INTAKE_STATUS.PENDING_REVIEW,
                      }));
                    } catch (error) {
                      console.error('Error undoing non-admit:', error);
                      alert('Failed to undo. Please try again.');
                    } finally {
                      setIsUpdatingStatus(false);
                    }
                  }}
                >
                  {isUpdatingStatus && (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  )}
                  Undo Non-admit
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Validation Checklist Row */}
        <div className='px-6 pb-3 pt-2 border-t border-gray-100'>
          <div className='flex items-center gap-2 mb-2'>
            <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide'>
              Document Validation
            </span>
            <div className='flex-1 h-px bg-gray-200'></div>
          </div>
          <div className='flex flex-wrap gap-2'>
            <ChecklistItem
              label='Order Attached'
              status={checklistStatuses.orderAttached}
              details={checklistDetails.orderAttached}
            />
            <ChecklistItem
              label='Order Signed'
              status={checklistStatuses.orderSigned}
              details={checklistDetails.orderSigned}
            />
            <ChecklistItem
              label='F2F Attached'
              status={checklistStatuses.f2fAttached}
              details={checklistDetails.f2fAttached}
            />
            <ChecklistItem
              label='F2F Signed'
              status={checklistStatuses.f2fSigned}
              details={checklistDetails.f2fSigned}
            />
            <ChecklistItem label='H&P' status={checklistStatuses.hAndP} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 flex gap-4 p-4'>
        {/* Documents Section */}
        {!isFormExpanded && (
          <div
            ref={pdfWrapperRef}
            className='w-1/2'
            style={isPdfSticky ? { height: pdfStickyHeight } : undefined}
          >
            <div
              className='h-screen'
              style={
                isPdfSticky
                  ? {
                      position: 'fixed',
                      top: 0,
                      left: pdfStickyLeft,
                      width: pdfStickyWidth,
                      zIndex: 10,
                    }
                  : undefined
              }
            >
              <PDFViewerNative
                documents={(selectedIntake.documents || []).map(doc => ({
                  id: doc.id,
                  fileName: doc.file_name,
                  isPersisted: true,
                  usedInExtraction: doc.used_in_extraction ?? true,
                }))}
                documentUrls={documentUrls}
                activeDocumentId={activeDocumentId}
                onActiveDocumentChange={setActiveDocumentId}
                onDocumentRename={handleDocumentRename}
                onDocumentAdd={handleDocumentAdd}
                onRefreshUrl={handleRefreshDocumentUrl}
              />
            </div>
          </div>
        )}

        {/* Extracted Information */}
        <div className={`${isFormExpanded ? 'w-full' : 'w-1/2'} flex flex-col`}>
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className='flex flex-col h-full'
          >
            {/* Workflow Navigation Header */}
            <div className='flex items-center gap-2 mb-4'>
              {/* PDF Toggle - compact icon button */}
              <button
                onClick={() => setIsFormExpanded(!isFormExpanded)}
                title={isFormExpanded ? 'Show PDF viewer' : 'Hide PDF viewer'}
                className='flex-shrink-0 p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors'
              >
                {isFormExpanded ? (
                  <PanelLeftOpen className='h-4 w-4' />
                ) : (
                  <PanelLeftClose className='h-4 w-4' />
                )}
              </button>

              <div className='flex-1 min-w-0'>
                <WorkflowStepper
                  steps={
                    [
                      {
                        id: INTAKE_TAB_NAMES.EXTRACTION,
                        label: 'Extraction',
                        shortLabel: 'Extraction',
                        isComplete: isTabComplete('extraction'),
                        isAccessible: true,
                      },
                      ...(hasEligibilityFeatureFlag
                        ? [
                            {
                              id: INTAKE_TAB_NAMES.ELIGIBILITY,
                              label: 'Eligibility',
                              shortLabel: 'Eligibility',
                              isComplete: isTabComplete('eligibility'),
                              isAccessible: canAccessEligibilityCheck(),
                            },
                          ]
                        : []),
                      ...(hasPriorAuthFeatureFlag
                        ? [
                            {
                              id: INTAKE_TAB_NAMES.PRIOR_AUTH,
                              label: 'Prior Authorization',
                              shortLabel: 'Prior Auth',
                              isComplete: isTabComplete('prior_auth'),
                              isAccessible: canAccessPriorAuth(),
                            },
                          ]
                        : []),
                      ...(hasReadyToCallFeatureFlag
                        ? [
                            {
                              id: INTAKE_TAB_NAMES.READY_TO_CALL,
                              label: 'Ready to Call',
                              shortLabel: 'Ready to Call',
                              isComplete: isTabComplete('ready_to_call'),
                              isAccessible: canAccessReadyToCall(),
                            },
                          ]
                        : []),
                      ...(hasSchedulingFeatureFlag
                        ? [
                            {
                              id: INTAKE_TAB_NAMES.SCHEDULING,
                              label: 'Scheduling',
                              shortLabel: 'Scheduling',
                              isComplete: isTabComplete('scheduling'),
                              isAccessible: canAccessScheduling(),
                            },
                          ]
                        : []),
                      ...(hasSchedulingFeatureFlag
                        ? [
                            {
                              id: INTAKE_TAB_NAMES.PUSH_TO_EMR,
                              label: 'Push to EMR',
                              shortLabel: 'Push to EMR',
                              isComplete: isTabComplete('push_to_emr'),
                              isAccessible: canAccessCreateEpisode(),
                            },
                          ]
                        : []),
                    ] as WorkflowStep[]
                  }
                  activeStepId={activeTab}
                  onStepClick={stepId => handleTabChange(stepId)}
                />
              </div>
            </div>

            {/* Hidden TabsList for accessibility - actual navigation handled by stepper */}
            <TabsList className='hidden'>
              <TabsTrigger value={INTAKE_TAB_NAMES.EXTRACTION}>
                Extract
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.ELIGIBILITY}>
                Eligibility
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.PRIOR_AUTH}>
                Prior Auth
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.READY_TO_CALL}>
                Call
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.SCHEDULING}>
                Schedule
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.PUSH_TO_EMR}>
                Push to EMR
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value={INTAKE_TAB_NAMES.EXTRACTION}
              className='flex-1 overflow-hidden'
            >
              <ExtractedFields
                intake={selectedIntake as unknown as IntakeResponse}
                intakeId={intakeId!}
                formData={formData}
                setFormData={setFormData}
                onConfirm={handleExtractionStatusChange}
                onIntakeUpdate={() => loadIntakeDetails(intakeId!)}
                agencyInsuranceOptions={agencyInsuranceOptions}
                agencyInsurancesLoading={agencyInsurancesLoading}
                agencyInsurancesError={agencyInsurancesError}
                agencyClinicOptions={agencyClinicOptions}
                agencyClinicsLoading={agencyClinicsLoading}
                agencyClinicsError={agencyClinicsError}
                visibleTabCount={visibleTabCount}
                documentNameDict={documentNameDict}
              />
            </TabsContent>

            {hasEligibilityFeatureFlag && (
              <TabsContent
                value={INTAKE_TAB_NAMES.ELIGIBILITY}
                className='flex-1 overflow-hidden'
              >
                {canAccessEligibilityCheck() ? (
                  <EligibilityCheckStep
                    selectedIntake={selectedIntake}
                    onRefresh={async () => {
                      await loadIntakeDetails(intakeId!);
                    }}
                    onStatusChange={async () => {
                      await loadIntakeDetails(intakeId!);
                    }}
                    agencyInsuranceOptions={agencyInsuranceOptions}
                    agencyInsurancesLoading={agencyInsurancesLoading}
                    agencyInsurancesError={agencyInsurancesError}
                  />
                ) : (
                  <Card className='h-full flex items-center justify-center'>
                    <CardContent className='text-center'>
                      <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                      <p className='text-sm font-medium text-gray-900 mb-1'>
                        Confirmation Required
                      </p>
                      <p className='text-xs text-gray-500'>
                        Complete and confirm the extracted information first
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}

            {hasPriorAuthFeatureFlag && (
              <TabsContent
                value={INTAKE_TAB_NAMES.PRIOR_AUTH}
                className='flex-1 overflow-hidden'
              >
                {canAccessPriorAuth() ? (
                  <PriorAuthStep
                    selectedIntake={selectedIntake}
                    agencyInsurances={agencyInsurances}
                    agencyInsurancesLoading={agencyInsurancesLoading}
                    onStatusChange={async () => {
                      await loadIntakeDetails(intakeId!);
                    }}
                  />
                ) : (
                  <Card className='h-full flex items-center justify-center'>
                    <CardContent className='text-center'>
                      <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                      <p className='text-sm font-medium text-gray-900 mb-1'>
                        Confirmation Required
                      </p>
                      <p className='text-xs text-gray-500'>
                        Complete eligibility check first
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}

            {hasReadyToCallFeatureFlag && (
              <TabsContent
                value={INTAKE_TAB_NAMES.READY_TO_CALL}
                className='flex-1 overflow-hidden'
              >
                {canAccessReadyToCall() ? (
                  <Card className='h-full flex flex-col'>
                    <CardHeader className='border-b py-3 px-4'>
                      <div className='flex items-center justify-between'>
                        <div className='text-sm font-medium'>Ready to Call</div>
                        <MarkCompletedButton
                          intakeId={intakeId!}
                          tabKey='ready_to_call'
                          isComplete={isTabComplete('ready_to_call')}
                          onStatusChange={async () => {
                            await loadIntakeDetails(intakeId!);
                          }}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
                      <div className='space-y-6'>
                        {/* Patient Contact Information */}
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                          <div className='border px-4 py-3'>
                            <div className='font-semibold mb-1'>
                              Contact Information
                            </div>
                            <div className='text-sm space-y-1'>
                              <div>
                                <span className='font-medium'>Patient:</span>{' '}
                                {selectedIntake.patient_first_name}{' '}
                                {selectedIntake.patient_last_name}
                              </div>
                              <div>
                                <span className='font-medium'>Phone:</span>{' '}
                                {selectedIntake.patient_phone || (
                                  <span className='text-orange-700'>
                                    Not provided
                                  </span>
                                )}
                              </div>
                              {selectedIntake.patient_email && (
                                <div>
                                  <span className='font-medium'>Email:</span>{' '}
                                  {selectedIntake.patient_email}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className='border px-4 py-3'>
                            <div className='font-semibold mb-1'>Address</div>
                            <div className='text-sm space-y-1'>
                              {selectedIntake.patient_street_address ? (
                                <>
                                  <div>
                                    {selectedIntake.patient_street_address}
                                  </div>
                                  <div>
                                    {[
                                      selectedIntake.patient_city,
                                      selectedIntake.patient_state,
                                    ]
                                      .filter(Boolean)
                                      .join(', ')}
                                    {selectedIntake.patient_zip_code &&
                                      ` ${selectedIntake.patient_zip_code}`}
                                  </div>
                                </>
                              ) : (
                                <div className='text-orange-700'>
                                  No address provided
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Insurance Information */}
                        <div className='border px-4 py-3'>
                          <div className='font-semibold mb-2'>Insurance</div>
                          <div className='space-y-2'>
                            <VirtualizedFormSelect
                              label='Agency Insurance'
                              placeholder={
                                agencyInsurancesLoading
                                  ? 'Loading agency insurances...'
                                  : agencyInsurancesError
                                    ? 'Error loading agency insurances'
                                    : 'Select agency insurance'
                              }
                              options={agencyInsuranceOptions}
                              disabled={agencyInsurancesLoading}
                              name='agency_insurance_id'
                              defaultValue={
                                selectedIntake.agency_insurance_id || ''
                              }
                              onChange={async e => {
                                const newValue = e.target.value;
                                await updateIntakeRecord(intakeId!, {
                                  agency_insurance_id: newValue || null,
                                });
                                setFormData(prev => ({
                                  ...prev,
                                  agency_insurance_id: newValue || null,
                                }));
                              }}
                            />
                            <div className='text-xs text-gray-500'>
                              ✓ = supports eligibility checks
                            </div>
                            <AiExtractedInsuranceSuggestion
                              value={selectedIntake.primary_insurance}
                            />
                            {selectedIntake.primary_member_id && (
                              <div className='text-sm'>
                                <span className='font-medium'>Member ID:</span>{' '}
                                {selectedIntake.primary_member_id}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Comments Section */}
                        <div className='border px-4 py-3'>
                          <div className='flex items-center gap-2 mb-3'>
                            <MessageSquare className='h-4 w-4 text-muted-foreground' />
                            <div className='font-semibold'>Comments</div>
                          </div>
                          <AutoSaveComments
                            value={selectedIntake.comments || ''}
                            onSave={async (newComments: string) => {
                              await updateIntakeRecord(intakeId!, {
                                comments: newComments,
                              });
                              setFormData(prev => ({
                                ...prev,
                                comments: newComments,
                              }));
                            }}
                            placeholder='Add notes or comments about this intake...'
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className='h-full flex items-center justify-center'>
                    <CardContent className='text-center'>
                      <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                      <p className='text-sm font-medium text-gray-900 mb-1'>
                        Confirmation Required
                      </p>
                      <p className='text-xs text-gray-500'>
                        Complete and confirm the extracted information first
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}

            {hasSchedulingFeatureFlag && (
              <TabsContent
                value={INTAKE_TAB_NAMES.SCHEDULING}
                className='flex-1 overflow-hidden'
              >
                {canAccessScheduling() ? (
                  <SchedulingStep
                    onStatusChange={async () => {
                      await loadIntakeDetails(intakeId!);
                    }}
                  />
                ) : (
                  <Card className='h-full flex items-center justify-center'>
                    <CardContent className='text-center'>
                      <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                      <p className='text-sm font-medium text-gray-900 mb-1'>
                        Confirmation Required
                      </p>
                      <p className='text-xs text-gray-500'>
                        Complete and confirm the extracted information first
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}

            {hasSchedulingFeatureFlag && (
              <TabsContent
                value={INTAKE_TAB_NAMES.PUSH_TO_EMR}
                className='flex-1 overflow-hidden'
              >
                {canAccessCreateEpisode() ? (
                  <CreateEpisodeStep
                    onComplete={async () => {
                      await loadIntakeDetails(intakeId!);
                    }}
                    onStatusChange={async () => {
                      await loadIntakeDetails(intakeId!);
                    }}
                  />
                ) : (
                  <Card className='h-full flex items-center justify-center'>
                    <CardContent className='text-center'>
                      <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                      <p className='text-sm font-medium text-gray-900 mb-1'>
                        Confirmation Required
                      </p>
                      <p className='text-xs text-gray-500'>
                        Complete and confirm the extracted information first
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Assignee Dialog */}
      <IntakeUserAssignmentDialog
        open={isAssigneeDialogOpen}
        onOpenChange={setIsAssigneeDialogOpen}
        intakes={selectedIntake ? [selectedIntake] : []}
        onSuccess={async () => {
          await loadIntakeDetails(intakeId!);
        }}
      />
    </div>
  );
};

export default Intake3;
